import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { httpService } from "../../services/httpService";
import { LOCAL_STORAGE_KEYS } from "../../shared/constants";
import { useAuth } from "../../hooks";
import { useTranslation } from "react-i18next";
import { SignOut as SignOutIcon } from "@phosphor-icons/react";
import AnimatedModal from "../../components/AnimatedModal";

// ── Types ─────────────────────────────────────────────────────────────────────

type SetupStatus = "connecting" | "in_progress" | "pending" | "completed" | "failed";

interface ProgressPayload {
  type?: string;
  status?: SetupStatus;
  message?: string;
  progress?: number;
}

// ── Confetti particle ─────────────────────────────────────────────────────────

interface Particle {
  id: number;
  x: number;
  color: string;
  delay: number;
  duration: number;
  size: number;
  rotation: number;
}

const COLORS = ["#1a3d7c", "#2a5ba8", "#30cd73", "#f59e0b", "#ec4899", "#8b5cf6", "#06b6d4"];

function generateParticles(count: number): Particle[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    delay: Math.random() * 0.6,
    duration: 2.2 + Math.random() * 1.2,
    size: 6 + Math.random() * 8,
    rotation: Math.random() * 360,
  }));
}

// ── Steps metadata ────────────────────────────────────────────────────────────

const STEP_LABELS = [
  { min: 0, max: 9, label: "Queuing your workspace…" },
  { min: 10, max: 29, label: "Creating database schema…" },
  { min: 30, max: 69, label: "Running migrations…" },
  { min: 70, max: 89, label: "Setting up scheduled tasks…" },
  { min: 90, max: 99, label: "Syncing user data…" },
  { min: 100, max: 100, label: "All done!" },
];

function stepLabel(progress: number) {
  return STEP_LABELS.find((s) => progress >= s.min && progress <= s.max)?.label ?? "Setting up…";
}

// ── Confetti overlay ──────────────────────────────────────────────────────────

function Confetti() {
  const [particles] = useState(() => generateParticles(80));
  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          initial={{ y: -20, x: `${p.x}vw`, opacity: 1, rotate: p.rotation }}
          animate={{ y: "110vh", opacity: [1, 1, 0], rotate: p.rotation + 720 }}
          transition={{ duration: p.duration, delay: p.delay, ease: "easeIn" }}
          style={{
            position: "absolute",
            top: 0,
            width: p.size,
            height: p.size * 0.5,
            borderRadius: 2,
            backgroundColor: p.color,
          }}
        />
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 3000;
const REDIRECT_DELAY_MS = 2800;

export default function WorkspaceSetup() {
  const wsUrl = import.meta.env.VITE_WEB_SOCKET_URL as string;
  const { t } = useTranslation();
  const { logout } = useAuth();

  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState("Connecting…");
  const [status, setStatus] = useState<SetupStatus>("connecting");
  const [showConfetti, setShowConfetti] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const doneRef = useRef(false);

  const handleLogout = () => {
    logout();
  };

  const handleCompleted = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    setProgress(100);
    setMessage("Setup complete!");
    setStatus("completed");
    setShowConfetti(true);
    setShowWelcome(true);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.WORKSPACE_SETUP_PENDING);
    setTimeout(() => {
      window.location.href = "/dashboard";
    }, REDIRECT_DELAY_MS);
  }, []);

  // ── Polling fallback ──────────────────────────────────────────────────────

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const data = await httpService.get<{ schema_setup: ProgressPayload["status"] extends string ? { status: string; message: string; progress: number } | null : never }>("/users/me");
        const setup = (data as { schema_setup?: { status: string; message: string; progress: number } | null }).schema_setup;
        if (!setup || setup.status === "completed") {
          clearInterval(pollRef.current!);
          pollRef.current = null;
          handleCompleted();
        } else if (setup.status === "failed") {
          clearInterval(pollRef.current!);
          pollRef.current = null;
          setStatus("failed");
          setMessage(setup.message || "Setup failed. Please contact support.");
        } else {
          setProgress(setup.progress ?? 0);
          setMessage(setup.message ?? stepLabel(setup.progress ?? 0));
          setStatus((setup.status as SetupStatus) ?? "in_progress");
        }
      } catch {
        // keep polling
      }
    }, POLL_INTERVAL_MS);
  }, [handleCompleted]);

  // ── WebSocket connection ──────────────────────────────────────────────────

  useEffect(() => {
    const token = httpService.getAccessToken();
    if (!token || !wsUrl) {
      startPolling();
      return;
    }

    const url = `${wsUrl}/task-progress/?token=${token}&task_id=schema_setup`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => setStatus("in_progress");

    ws.onmessage = (evt) => {
      try {
        const data: ProgressPayload = JSON.parse(evt.data || "{}");
        if (data.type === "connection") return;
        if (typeof data.progress === "number") setProgress(data.progress);
        if (data.message) setMessage(data.message);
        if (data.status === "completed") {
          ws.close();
          handleCompleted();
        } else if (data.status === "failed") {
          ws.close();
          setStatus("failed");
          setMessage(data.message || "Setup failed. Please contact support.");
        } else if (data.status) {
          setStatus(data.status);
        }
      } catch {
        // ignore malformed
      }
    };

    ws.onerror = () => startPolling();

    ws.onclose = (evt) => {
      // 4001 = token invalid; fall back to polling
      if (evt.code === 4001 || evt.code === 4003) startPolling();
    };

    return () => {
      ws.close();
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [wsUrl, handleCompleted, startPolling]);

  // ── Render ────────────────────────────────────────────────────────────────

  const isFailed = status === "failed";
  const displayProgress = Math.min(progress, 100);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden"
      style={{ background: "linear-gradient(160deg,#eef2fc 0%,#f5f7fd 40%,#edf1fb 70%,#f2f5fd 100%)" }}
    >
      {/* Grid overlay */}
      <div aria-hidden className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "linear-gradient(rgba(26,61,124,0.035) 1px,transparent 1px),linear-gradient(90deg,rgba(26,61,124,0.035) 1px,transparent 1px)",
          backgroundSize: "52px 52px",
        }}
      />

      {/* Confetti */}
      <AnimatePresence>{showConfetti && <Confetti />}</AnimatePresence>

      {/* Logout Button */}
      <div className="fixed top-6 right-6 z-50">
        <button
          onClick={() => setShowLogoutModal(true)}
          className="inline-flex items-center gap-[6px] px-4 py-2 rounded-lg border border-red-200 bg-[rgba(255,255,255,0.85)] backdrop-blur-[6px] text-red-600 text-[13px] font-semibold cursor-pointer transition-colors duration-150 hover:bg-red-50 shadow-sm"
        >
          {t("common.logout", "Logout")}
          <SignOutIcon size={16} weight="bold" />
        </button>
      </div>

      <AnimatedModal
        isConfirmation
        show={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        title={t("common.confirm_logout", "Confirm Logout")}
        confirmText={t("common.logout", "Logout")}
        confirmButtonColor="red"
      >
        <div className="text-gray-700 text-center">
          {t("common.logout_confirmation_message", "Are you sure you want to log out?")}
        </div>
      </AnimatedModal>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
        className="relative z-10 w-full max-w-[480px] mx-4"
      >
        <div className="relative rounded-[28px] p-[1.5px] shadow-[0_12px_56px_rgba(26,61,124,0.18)]"
          style={{ background: "linear-gradient(135deg,#1a3d7c,#2a5ba8 50%,#30cd73)" }}
        >
          <div className="rounded-[27px] backdrop-blur-[12px] px-10 py-12 flex flex-col items-center gap-6 overflow-hidden" style={{ backgroundColor: "rgba(255,255,255,0.96)" }}>

            {/* Glow blobs */}
            <div aria-hidden className="absolute -top-12 -left-12 w-48 h-48 rounded-full opacity-15 pointer-events-none"
              style={{ background: "radial-gradient(circle,#2a5ba8,transparent 70%)" }} />
            <div aria-hidden className="absolute -bottom-12 -right-12 w-48 h-48 rounded-full opacity-15 pointer-events-none"
              style={{ background: "radial-gradient(circle,#30cd73,transparent 70%)" }} />

            {/* Icon / success check */}
            <AnimatePresence mode="wait">
              {showWelcome ? (
                <motion.div
                  key="check"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 260, damping: 18 }}
                  className="w-[72px] h-[72px] rounded-full flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg,#1a3d7c,#30cd73)" }}
                >
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path d="M7 16l7 7 11-13" stroke="white" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </motion.div>
              ) : (
                <motion.div
                  key="spinner"
                  className="w-[72px] h-[72px] rounded-full flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg,rgba(26,61,124,0.10),rgba(48,205,115,0.10))" }}
                >
                  <motion.svg
                    width="36" height="36" viewBox="0 0 36 36" fill="none"
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1.4, ease: "linear" }}
                  >
                    <circle cx="18" cy="18" r="14" stroke="rgba(26,61,124,0.12)" strokeWidth="3" />
                    <path d="M18 4 A14 14 0 0 1 32 18" stroke="url(#sg)" strokeWidth="3" strokeLinecap="round" />
                    <defs>
                      <linearGradient id="sg" x1="18" y1="4" x2="32" y2="18" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#1a3d7c" />
                        <stop offset="1" stopColor="#30cd73" />
                      </linearGradient>
                    </defs>
                  </motion.svg>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Heading */}
            <AnimatePresence mode="wait">
              {showWelcome ? (
                <motion.div
                  key="welcome"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center"
                >
                  <p className="text-[22px] font-black tracking-[-0.02em] mb-1"
                    style={{
                      background: "linear-gradient(118deg,#1a3d7c 0%,#2a5ba8 50%,#30cd73 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    You're all set!
                  </p>
                  <p className="text-[13.5px] text-[#64748b]">Redirecting you to your dashboard…</p>
                </motion.div>
              ) : (
                <motion.div key="progress-text" className="text-center w-full">
                  <p className="text-[20px] font-black tracking-[-0.02em] mb-1"
                    style={{
                      background: "linear-gradient(118deg,#1a3d7c 0%,#2a5ba8 50%,#30cd73 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {isFailed ? "Setup failed" : "Setting up your workspace"}
                  </p>
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={message}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.22 }}
                      className={`text-[13px] font-medium ${isFailed ? "text-red-500" : "text-[#64748b]"}`}
                    >
                      {message}
                    </motion.p>
                  </AnimatePresence>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Progress bar (hidden once complete) */}
            {!showWelcome && !isFailed && (
              <div className="w-full">
                <div className="h-[6px] rounded-full bg-[rgba(26,61,124,0.08)] overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: "linear-gradient(90deg,#1a3d7c,#2a5ba8 50%,#30cd73)" }}
                    initial={{ width: "0%" }}
                    animate={{ width: `${displayProgress}%` }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                </div>
                <div className="flex justify-between mt-2">
                  <span className="text-[11px] text-[#94a3b8]">{stepLabel(displayProgress)}</span>
                  <span className="text-[11px] font-bold text-[#1a3d7c]">{displayProgress}%</span>
                </div>
              </div>
            )}

            {/* Step dots */}
            {!showWelcome && !isFailed && (
              <div className="flex gap-2">
                {STEP_LABELS.slice(0, -1).map((s, i) => {
                  const filled = displayProgress >= s.max;
                  const active = displayProgress >= s.min && displayProgress <= s.max;
                  return (
                    <motion.div
                      key={i}
                      className="rounded-full"
                      animate={{
                        width: active ? 20 : 8,
                        backgroundColor: filled ? "#30cd73" : active ? "#2a5ba8" : "rgba(26,61,124,0.15)",
                      }}
                      style={{ height: 8 }}
                      transition={{ duration: 0.3 }}
                    />
                  );
                })}
              </div>
            )}

            {/* Error state CTA */}
            {isFailed && (
              <a
                href="mailto:support@apprevbooster.com"
                className="inline-flex items-center px-6 py-[10px] rounded-full text-white text-[13.5px] font-semibold"
                style={{ background: "linear-gradient(118deg,#1a3d7c,#2a5ba8 55%,#30cd73)" }}
              >
                Contact Support
              </a>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
